import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Button, Card, Chip, Collapse, Grid, IconButton,
    MenuItem, TextField, Typography, Alert, Stack,
    Tooltip, Badge,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import HistoryIcon from "@mui/icons-material/History";

import AuditStatusChip from "../components/AuditStatusChip";
import AuditPrioridadChip from "../components/AuditPrioridadChip";
import { useAuditUser } from "../hooks/useAuditUser";
import { getItems, getEstados } from "../services/audit.service";

const fmt = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("es-AR", {
        day: "2-digit", month: "2-digit", year: "numeric",
    });
};

const isVencido = (f) => f && new Date(f) < new Date();

const PRIORIDAD_COLOR = {
    BAJA: "#22c55e", MEDIA: "#f59e0b", ALTA: "#f97316", CRITICA: "#ef4444",
};

const FILTROS_INIT = { status_id: "", prioridad: "", desde: "", hasta: "" };

function KpiButton({ label, value, color, activo, onClick, icon }) {
    return (
        <Box onClick={onClick} sx={{
            display: "flex", alignItems: "center", gap: 0.75,
            px: 1.25, py: 0.6, borderRadius: 2, border: "1px solid",
            borderColor: activo ? color : "#e2e8f0",
            backgroundColor: activo ? `${color}14` : "#fff",
            cursor: "pointer", transition: "all 0.15s ease",
            "&:hover": { borderColor: color, backgroundColor: `${color}0e` },
            userSelect: "none",
        }}>
            <Box sx={{ color, display: "flex", alignItems: "center" }}>
                {React.cloneElement(icon, { sx: { fontSize: 15 } })}
            </Box>
            <Typography variant="body2" fontWeight={700}
                sx={{ color, lineHeight: 1, minWidth: 16, textAlign: "center" }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
        </Box>
    );
}

export default function AuditList() {
    const navigate = useNavigate();
    const { username, puedeCrear, puedeVerTodo } = useAuditUser();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [estados, setEstados] = useState([]);
    const [filtros, setFiltros] = useState(FILTROS_INIT);
    const [showFiltros, setShowFiltros] = useState(false);
    const [pagina, setPagina] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [kpiFiltro, setKpiFiltro] = useState(null);

    const kpiAbiertos = rows.filter(r => !["CERRADO", "RESUELTO"].includes(r.status_codigo)).length;
    const kpiVencidos = rows.filter(r =>
        isVencido(r.fecha_requerida) && !["CERRADO", "RESUELTO"].includes(r.status_codigo)).length;
    const kpiCerrados = rows.filter(r => ["CERRADO", "RESUELTO"].includes(r.status_codigo)).length;

    const rowsVisibles = (() => {
        if (kpiFiltro === "abiertos") return rows.filter(r => !["CERRADO", "RESUELTO"].includes(r.status_codigo));
        if (kpiFiltro === "vencidos") return rows.filter(r =>
            isVencido(r.fecha_requerida) && !["CERRADO", "RESUELTO"].includes(r.status_codigo));
        if (kpiFiltro === "cerrados") return rows.filter(r => ["CERRADO", "RESUELTO"].includes(r.status_codigo));
        return rows;
    })();

    const filtrosActivos = Object.values(filtros).filter(Boolean).length;

    useEffect(() => { getEstados().then(setEstados).catch(() => { }); }, []);

    const cargar = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const params = {
                username, page: pagina + 1, limit: pageSize,
                ...Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== "")),
            };
            const data = await getItems(params);
            setRows(data.data || []);
            setTotal(data.total || 0);
        } catch { setError("Error al cargar los ítems."); }
        finally { setLoading(false); }
    }, [username, pagina, pageSize, filtros]);

    useEffect(() => { cargar(); }, [cargar]);

    const handleFiltro = (e) => {
        setFiltros(p => ({ ...p, [e.target.name]: e.target.value }));
        setPagina(0); setKpiFiltro(null);
    };
    const limpiar = () => { setFiltros(FILTROS_INIT); setPagina(0); setKpiFiltro(null); };
    const toggleKpi = (key) => setKpiFiltro(prev => prev === key ? null : key);

    const columns = [
        {
            field: "_bar", headerName: "", width: 6, sortable: false, disableColumnMenu: true,
            renderCell: ({ row }) => (
                <Box sx={{
                    width: 4, height: "100%",
                    backgroundColor: PRIORIDAD_COLOR[row.prioridad] || "#e2e8f0",
                    borderRadius: "2px 0 0 2px", ml: "-10px"
                }} />
            ),
        },
        {
            field: "id", headerName: "#", width: 60,
            renderCell: ({ value }) => (
                <Typography variant="caption" fontWeight={700} color="text.disabled">{value}</Typography>
            ),
        },
        {
            field: "titulo", headerName: "Título", flex: 1, minWidth: 180,
            renderCell: ({ row }) => (
                <Stack direction="row" alignItems="center" gap={0.5}>
                    {isVencido(row.fecha_requerida) && !["CERRADO", "RESUELTO"].includes(row.status_codigo) && (
                        <Tooltip title="Vencido">
                            <WarningAmberIcon sx={{ fontSize: 13, color: "warning.main", flexShrink: 0 }} />
                        </Tooltip>
                    )}
                    <Typography variant="body2" noWrap title={row.titulo}>{row.titulo}</Typography>
                </Stack>
            ),
        },
        {
            field: "status_desc", headerName: "Estado", width: 165,
            renderCell: ({ row }) => (
                <AuditStatusChip codigo={row.status_codigo} descripcion={row.status_desc} color={row.status_color} />
            ),
        },
        {
            field: "sucursal_nombre", headerName: "Sucursal", width: 140,
            renderCell: ({ value }) => (
                <Typography variant="body2" noWrap color="text.secondary">{value}</Typography>
            ),
        },
        {
            field: "advertencia_nombre", headerName: "Advertencia", width: 200,
            renderCell: ({ value }) => value ? (
                <Typography variant="body2" noWrap title={value} color="warning.dark">
                    {value}
                </Typography>
            ) : (
                <Typography variant="caption" color="text.disabled">—</Typography>
            ),
        },
        {
            field: "prioridad", headerName: "Prioridad", width: 95,
            renderCell: ({ value }) => <AuditPrioridadChip prioridad={value} />,
        },
        {
            field: "fecha_deteccion", headerName: "Detección", width: 100,
            renderCell: ({ value }) => <Typography variant="caption">{fmt(value)}</Typography>,
        },
        {
            field: "fecha_requerida", headerName: "Límite", width: 100,
            renderCell: ({ row }) => (
                <Typography variant="caption"
                    fontWeight={isVencido(row.fecha_requerida) && !["CERRADO", "RESUELTO"].includes(row.status_codigo) ? 700 : 400}
                    color={isVencido(row.fecha_requerida) && !["CERRADO", "RESUELTO"].includes(row.status_codigo) ? "error.main" : "text.secondary"}>
                    {fmt(row.fecha_requerida)}
                </Typography>
            ),
        },
        ...(puedeVerTodo
            ? [{
                field: "controlador_nombre", headerName: "Controlador", width: 150,
                renderCell: ({ value }) => (
                    <Typography variant="body2" noWrap color={value ? "text.primary" : "text.disabled"}>
                        {value || "Sin asignar"}
                    </Typography>
                ),
            }]
            : [{
                field: "fue_derivado", headerName: "Asignación", width: 160,
                renderCell: ({ row }) => row.fue_derivado
                    ? (
                        <Tooltip title={`Derivado a: ${row.derivado_a_nombre || row.controlador_actual}`}>
                            <Chip icon={<SwapHorizIcon sx={{ fontSize: "0.85rem !important" }} />}
                                label={row.derivado_a_nombre || row.controlador_actual} size="small"
                                sx={{ backgroundColor: "#eff6ff", color: "#1d4ed8", maxWidth: 145 }} />
                        </Tooltip>
                    ) : (
                        <Chip label="Asignado a mí" size="small"
                            sx={{ backgroundColor: "#f0fdf4", color: "#166534" }} />
                    ),
            }]
        ),
        {
            field: "auditor_username", headerName: "Auditor", width: 110,
            renderCell: ({ value }) => (
                <Typography variant="caption" color="text.disabled" noWrap>{value}</Typography>
            ),
        },
    ];


    return (
        <Box sx={{ backgroundColor: "background.default", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

            {/* ── Header sticky ── */}
            <Box sx={{
                position: "sticky", top: 0, zIndex: 10,
                backgroundColor: "background.default",
                px: { xs: 1.5, md: 2.5 }, pt: { xs: 1.5, md: 2 }, pb: 1,
                borderBottom: "1px solid", borderColor: "divider",
            }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1} gap={1} flexWrap="wrap">
                    <Box>
                        <Typography variant="h5">Auditoría</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Investigaciones y seguimiento
                        </Typography>
                    </Box>

                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
                        <KpiButton label="Abiertos" value={kpiAbiertos} color="#f59e0b"
                            activo={kpiFiltro === "abiertos"} onClick={() => toggleKpi("abiertos")}
                            icon={<HourglassEmptyIcon />} />
                        <KpiButton label="Vencidos" value={kpiVencidos} color="#ef4444"
                            activo={kpiFiltro === "vencidos"} onClick={() => toggleKpi("vencidos")}
                            icon={<ErrorOutlineIcon />} />
                        <KpiButton label="Cerrados" value={kpiCerrados} color="#22c55e"
                            activo={kpiFiltro === "cerrados"} onClick={() => toggleKpi("cerrados")}
                            icon={<CheckCircleOutlineIcon />} />

                        {puedeVerTodo && (
                            <Tooltip title="Estadísticas">
                                <IconButton size="small" onClick={() => navigate("estadisticas")}
                                    sx={{
                                        color: "primary.main", border: "1px solid",
                                        borderColor: "#e2e8f0", borderRadius: 2, p: "5px",
                                        "&:hover": { borderColor: "primary.main", backgroundColor: "primary.light" },
                                    }}>
                                    <BarChartIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Tooltip>
                        )}

                        {puedeVerTodo && (
                            <Tooltip title="Log de auditoría">
                                <IconButton size="small" onClick={() => navigate("log")}
                                    sx={{
                                        color: "#64748b", border: "1px solid",
                                        borderColor: "#e2e8f0", borderRadius: 2, p: "5px",
                                        "&:hover": { borderColor: "#64748b", backgroundColor: "#f8fafc" },
                                    }}>
                                    <HistoryIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Box sx={{ width: "1px", height: 20, backgroundColor: "#e2e8f0", mx: 0.5, flexShrink: 0 }} />

                        <Tooltip title="Actualizar">
                            <IconButton onClick={cargar} disabled={loading}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Badge variant="dot" color="primary" invisible={!filtrosActivos}>
                            <Button variant="outlined" startIcon={<FilterListIcon />}
                                onClick={() => setShowFiltros(v => !v)}
                                color={showFiltros ? "primary" : "inherit"}>
                                Filtros
                            </Button>
                        </Badge>
                        {puedeCrear && (
                            <Button variant="contained" startIcon={<AddIcon />}
                                onClick={() => navigate("nuevo")}>
                                Nuevo ítem
                            </Button>
                        )}
                    </Stack>
                </Stack>

                <Collapse in={showFiltros}>
                    <Card sx={{ mb: 1 }}>
                        <Box sx={{ p: "10px 14px" }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="overline">Filtros</Typography>
                                <IconButton size="small" onClick={() => setShowFiltros(false)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                            <Grid container spacing={1.5} alignItems="flex-end">
                                <Grid item xs={12} sm={6} md={3}>
                                    <TextField select fullWidth label="Estado" name="status_id"
                                        value={filtros.status_id} onChange={handleFiltro}>
                                        <MenuItem value="">Todos</MenuItem>
                                        {estados.map(e => (
                                            <MenuItem key={e.id} value={e.id}>
                                                <Stack direction="row" alignItems="center" gap={1}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: e.color, flexShrink: 0 }} />
                                                    {e.descripcion}
                                                </Stack>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField select fullWidth label="Prioridad" name="prioridad"
                                        value={filtros.prioridad} onChange={handleFiltro}>
                                        <MenuItem value="">Todas</MenuItem>
                                        <MenuItem value="BAJA">🟢 Baja</MenuItem>
                                        <MenuItem value="MEDIA">🟡 Media</MenuItem>
                                        <MenuItem value="ALTA">🟠 Alta</MenuItem>
                                        <MenuItem value="CRITICA">🔴 Crítica</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField fullWidth type="date" label="Desde" name="desde"
                                        value={filtros.desde} onChange={handleFiltro}
                                        InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField fullWidth type="date" label="Hasta" name="hasta"
                                        value={filtros.hasta} onChange={handleFiltro}
                                        InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Button variant="text" color="inherit" onClick={limpiar} size="small">
                                        Limpiar filtros
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Card>
                </Collapse>

                {kpiFiltro && (
                    <Stack direction="row" alignItems="center" gap={1} mt={0.5}>
                        <Typography variant="caption" color="text.secondary">Mostrando solo:</Typography>
                        <Chip
                            label={kpiFiltro === "abiertos" ? "Abiertos" : kpiFiltro === "vencidos" ? "Vencidos" : "Cerrados"}
                            size="small" onDelete={() => setKpiFiltro(null)}
                            sx={{
                                backgroundColor: kpiFiltro === "abiertos" ? "#fff7ed" : kpiFiltro === "vencidos" ? "#fef2f2" : "#f0fdf4",
                                color: kpiFiltro === "abiertos" ? "#c2410c" : kpiFiltro === "vencidos" ? "#b91c1c" : "#166534",
                                fontWeight: 600,
                            }}
                        />
                    </Stack>
                )}

                {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>}
            </Box>

            {/* ── Contenido scrolleable ── */}
            <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2, flex: 1 }}>
                <Card sx={{ overflow: "hidden" }}>
                    <DataGrid
                        rows={rowsVisibles}
                        columns={columns}
                        rowCount={total}
                        loading={loading}
                        paginationMode="server"
                        paginationModel={{ page: pagina, pageSize }}
                        onPaginationModelChange={({ page, pageSize: ps }) => { setPagina(page); setPageSize(ps); }}
                        pageSizeOptions={[10, 20, 50]}
                        disableRowSelectionOnClick
                        onRowClick={({ row }) => navigate(`${row.id}`)}
                        getRowId={r => r.id}
                        getRowClassName={({ row }) => row.fue_derivado ? "row-derivado" : ""}
                        rowHeight={46}
                        hideFooterSelectedRowCount
                        sx={{
                            border: "none",
                            "& .row-derivado": { backgroundColor: "#fafbff", "&:hover": { backgroundColor: "#eff6ff" } },
                        }}
                        localeText={{
                            noRowsLabel: "No hay ítems para mostrar",
                            MuiTablePagination: {
                                labelRowsPerPage: "Por página:",
                                labelDisplayedRows: ({ from, to, count }) => `${from}–${to} de ${count}`,
                            },
                        }}
                    />
                </Card>

                {!puedeVerTodo && (
                    <Stack direction="row" gap={1.5} mt={1.5} alignItems="center" flexWrap="wrap">
                        <Typography variant="caption" color="text.disabled">Referencias:</Typography>
                        <Chip label="Asignado a mí" size="small"
                            sx={{ backgroundColor: "#f0fdf4", color: "#166534", fontSize: "0.68rem" }} />
                        <Chip icon={<SwapHorizIcon sx={{ fontSize: "0.8rem !important" }} />}
                            label="Derivado" size="small"
                            sx={{ backgroundColor: "#eff6ff", color: "#1d4ed8", fontSize: "0.68rem" }} />
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <WarningAmberIcon sx={{ fontSize: 12, color: "warning.main" }} />
                            <Typography variant="caption" color="text.disabled">Vencido</Typography>
                        </Stack>
                    </Stack>
                )}
            </Box>
        </Box>
    );
}